import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import { Inject } from 'inversify-props'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import { criarEmissaoDeNota, criarVendaDoPdv } from '@/shareds/venda-shareds'
import { Desconto, ItemDaVenda, TabelaDePreco, UsuarioBase } from "@/models"
import { FindModalidadeDeVendaUseCase, type VendaServiceAdapter } from '../venda'
import type { PreVendaServiceAdapter } from './adapter'
import { PreVenda } from '@/models/pre-venda/PreVenda'
import AplicarTabelaDePrecoUseCase from '../tabela-de-precos/AplicarTabelaDePrecoUseCase'
import AplicarPrecoDeCustoUseCase from '../tabela-de-precos/AplicarPrecoDeCustoUseCase'
import type { ClienteServiceAdapter } from '../cliente'
import { resolverDestinatarioDaPreVenda } from '@/shareds/pre-venda-shareds'
import { tabelaDePrecoEstaVigente } from '@/shareds/tabela-de-preco-shareds'
import { avisarCreditoInformadoDoCliente } from '../venda/ValidarCreditoInformadoUseCase'

export type ResultadoImportacaoPreVenda = {
	preVendaId: string
	representante: UsuarioBase | null
	isAtacado: boolean
	tabelaDePreco: TabelaDePreco | null
}
export class ImportarPreVendaUseCase {
	@Inject('PreVendaServiceAdapter')
	private preVendaService!: PreVendaServiceAdapter

	@Inject('VendaServiceAdapter')
	private vendaService!: VendaServiceAdapter

	@Inject('ClienteServiceAdapter')
	private clienteService!: ClienteServiceAdapter

	findModalidadeDeVendaUseCase = new FindModalidadeDeVendaUseCase()


	async importarPreVenda(identificador: string, telaDeEmissaoDeNota: boolean, tabelaDePreco: TabelaDePreco | null) {
		try {

			let preVenda: PreVenda
			const loja = telaDeEmissaoDeNota
				? VendaModule.lojaDaEntrada
				: VendaModule.lojaDaVenda

			if (!loja) throw new Error('Loja não selecionada')
			if ((VendaModule.turnoDeVenda == null || !VendaModule.turnoDeVenda.pontoDeVenda) && !telaDeEmissaoDeNota) throw new Error('Pdv de venda não selecionado')

			if (telaDeEmissaoDeNota) {
				preVenda = await this.preVendaService.findPreVendaByFiltro({
					identificador: identificador,
					lojaId: loja.id,
					cnpjDestinatario: VendaModule.emissaoEntradaAtual?.cpfDoCliente,
				})
			} else {
				preVenda = await this.preVendaService.findPreVendaByIdentificador(identificador)
			}

			const cliente = preVenda.cpfOuCnpjDoCliente && preVenda.cpfOuCnpjDoCliente.length > 0
				? await this.clienteService.get(preVenda.cpfOuCnpjDoCliente, { gruposEconomicosId: [loja.grupoEconomico.id] })
				: null
			if (cliente?.inativo) return AlertModule.setError("Cliente da pré venda está inativo")
			const novoIdentificador = await this.vendaService.gerarIdentificadorDeVendaSemTurno(loja.id)

			const novaVenda = !telaDeEmissaoDeNota && VendaModule.turnoDeVenda
				? criarVendaDoPdv(VendaModule.turnoDeVenda.pontoDeVenda)
				: criarEmissaoDeNota()

			const vendaDaTela = telaDeEmissaoDeNota
				? VendaModule.emissaoEntradaAtual
				: VendaModule.vendaAtual

			const destinatario = resolverDestinatarioDaPreVenda(
				cliente,
				preVenda.cpfOuCnpjDoCliente,
				vendaDaTela
					? { cliente: vendaDaTela.cliente, cpfDoCliente: vendaDaTela.cpfDoCliente }
					: null,
			)
			novaVenda.cliente = destinatario.cliente
			novaVenda.cpfDoCliente = destinatario.cpfDoCliente

			if (cliente) {
				await avisarCreditoInformadoDoCliente(novaVenda, loja)
			}

			novaVenda.loja = loja.id
			novaVenda.identificador = novoIdentificador
			novaVenda.cuponsDeDesconto = []
			novaVenda.tipoDeTransacao = VendaModule.emissaoEntradaAtual?.tipoDeTransacao || 'Venda'
			novaVenda.isBrinde = VendaModule.vendaAtual?.isBrinde || false
			novaVenda.isConsignado = VendaModule.vendaAtual?.isConsignado || false
			novaVenda.isDemonstracao = VendaModule.vendaAtual?.isDemonstracao || false
			novaVenda.vendedor = preVenda.vendedor ? [preVenda.vendedor] : []

			const itensFormatados = preVenda.itens.map<ItemDaVenda>(item => ({
				id: '',
				desconto: {
					isPercentual: item.descontoIsPercentual ?? false,
					valor: item.descontoValor ?? 0,
				},
				quantidade: item.quantidade,
				quantidadeLida: 0,
				preco: item.preco,
				precoDeCusto: item.precoDeCusto ?? 0,
				produto: item.produto,
				idConjunto: null,
				descontoVarejo: 0,
				percentualCashback: item.percentualCashback ?? 0,
				diasParaEfetivarCashback: item.diasParaEfetivarCashback ?? 0,
				isBrinde: preVenda.tipoDePreVenda === 'brinde',
				idItemNotaOrigem: null,
				chaveNotaOrigem: item.chaveNotaOrigem,
				diasParaExpirarCashback: item.diasParaExpirarCashback ?? 0,
				possuiDescontoPorTabela: item.possuiDescontoPorTabela ?? false,
				valorDescontoRateadoDoItem: 0,
				itemDeDevolucao: false,
				isTotalmenteTrocado: false,
				motivoCancelamento: null,
				isCompra: false,
				idItemOrigem: '',
				isDaVendaReaberta: false,
				vendedor: preVenda.vendedor,
				autenticadoParaLiberarProdutoSemEstoque: false,
				vendaOrigemDoItem: null,
				valorFreteRateado: 0,
				isItemReferenteATroca: false,
				isDeOutraLoja: false,
				percentualDescontoTabelaPreco: item.percentualDescontoTabelaPreco ?? 0,
				precoDeCustoReal: 0,
				skuPai: null,
			}))

			let isProcessoTelaDeEmissaoDeNota = false
			switch (preVenda.tipoDePreVenda) {
				case 'venda':
					novaVenda.tipoDeTransacao = 'Venda'
					novaVenda.tipoNota = 'Saída'
					break
				case 'demonstracao':
					novaVenda.tipoDeTransacao = 'Venda'
					novaVenda.tipoNota = 'Saída'
					novaVenda.isDemonstracao = true
					break
				case 'consignacao':
					novaVenda.tipoDeTransacao = 'Venda'
					novaVenda.tipoNota = 'Saída'
					novaVenda.isConsignado = true
					break
				case 'outras saidas':
					novaVenda.tipoDeTransacao = 'Outros'
					novaVenda.tipoNota = 'Saída'
					isProcessoTelaDeEmissaoDeNota = true
					break
				case 'outras entradas':
					novaVenda.tipoDeTransacao = 'Outros'
					novaVenda.tipoNota = 'Entrada'
					break
				case 'brinde':
					novaVenda.tipoDeTransacao = 'Venda'
					novaVenda.tipoNota = 'Saída'
					novaVenda.isBrinde = true
					break
				case 'ativo':
					novaVenda.tipoDeTransacao = 'Ativo'
					novaVenda.tipoNota = 'Saída'
					isProcessoTelaDeEmissaoDeNota = true
					break
				case 'conserto':
					novaVenda.tipoDeTransacao = 'Conserto'
					novaVenda.tipoNota = 'Saída'
					isProcessoTelaDeEmissaoDeNota = true
					break
				case 'devolucao entrada':
					novaVenda.tipoDeTransacao = 'Devolução'
					novaVenda.tipoNota = 'Entrada'
					novaVenda.itens = novaVenda.itens.map(item => {
						return {
							...item,
							quantidade: item.quantidade > 0 ? -Math.abs(item.quantidade) : item.quantidade,
							preco: item.preco > 0 ? Math.abs(item.preco) : item.preco,

						}
					})
					break
				case 'devolucao saida':
					novaVenda.tipoDeTransacao = 'Devolução'
					novaVenda.tipoNota = 'Saída'
					novaVenda.itens = novaVenda.itens.map(item => {
						return {
							...item,
							quantidade: item.quantidade > 0 ? -Math.abs(item.quantidade) : item.quantidade,
							preco: item.preco > 0 ? Math.abs(item.preco) : item.preco,
						}
					})
					isProcessoTelaDeEmissaoDeNota = true
					break
				case 'transferencia':
					novaVenda.tipoDeTransacao = 'Transferência'
					novaVenda.tipoNota = 'Saída'
					isProcessoTelaDeEmissaoDeNota = true
					break
				case 'atacado':
					if (!telaDeEmissaoDeNota) {
						novaVenda.tipoDeTransacao = 'Venda'
					}
					novaVenda.tipoNota = 'Saída'
					novaVenda.isDemonstracao = false
					novaVenda.isConsignado = false
					novaVenda.isBrinde = false
					break
				default:
					novaVenda.tipoNota = 'Saída'
					novaVenda.isDemonstracao = false
					novaVenda.isConsignado = false
					novaVenda.isBrinde = false
			}

			if (!telaDeEmissaoDeNota && isProcessoTelaDeEmissaoDeNota) {
				throw new Error('Tipo de pré venda não pode ser importada no caixa, apenas na tela de emissão de notas')
			}

			let tabelaDePrecoParaAplicar: TabelaDePreco | undefined = undefined

			if (novaVenda.tipoDeTransacao === 'Transferência') {
				novaVenda.itens = await AplicarPrecoDeCustoUseCase({
					loja,
					itens: itensFormatados,
					tipoDeCliente: null,
				})
			} else if (novaVenda.tipoDeTransacao !== 'Devolução' && novaVenda.tipoNota === 'Saída') {

				if (preVenda.tabelaDePreco != null) {
					novaVenda.itens = itensFormatados
				} else {
					if (!novaVenda.isDemonstracao && novaVenda.tipoDeTransacao === 'Venda') {
						tabelaDePrecoParaAplicar = tabelaDePreco ?? undefined
					}

					if (tabelaDePrecoParaAplicar && !tabelaDePrecoEstaVigente(tabelaDePrecoParaAplicar)) {
						AlertModule.setInfo(
							`A tabela ${tabelaDePrecoParaAplicar.nome} está fora do prazo de vigência e não foi aplicada na importação.`,
						)
						tabelaDePrecoParaAplicar = undefined
					}

					if (tabelaDePrecoParaAplicar) {
						novaVenda.itens = await AplicarTabelaDePrecoUseCase({
							loja: loja,
							itens: itensFormatados,
							tipoDeCliente: null,
							validarLimiteDeProduto: false,
							vendaAtual: novaVenda,
							tabelaDePreco: tabelaDePrecoParaAplicar,
						})
					} else {
						const precisamReaplicar = itensFormatados.filter(item => !(item.preco != null && item.preco > 0))
						const itensReaplicados = precisamReaplicar.length
							? await AplicarTabelaDePrecoUseCase({
								loja: loja,
								itens: precisamReaplicar,
								tipoDeCliente: null,
								validarLimiteDeProduto: false,
								vendaAtual: novaVenda,
							})
							: []

						let idx = 0
						novaVenda.itens = itensFormatados.map(item =>
							(item.preco != null && item.preco > 0) ? item : itensReaplicados[idx++])
					}
				}
			} else {
				novaVenda.itens = itensFormatados
			}

			if (preVenda.tipoDePreVenda === 'venda' && (preVenda.descontoValor || 0) > 0) {
				novaVenda.desconto = {
					isPercentual: preVenda.descontoIsPercentual ?? false,
					valor: preVenda.descontoValor ?? 0,
				} as Desconto
			}

			novaVenda.idPreVenda = preVenda.id
			novaVenda.tabelaDePrecoManualId = preVenda.tabelaDePreco?.id ?? tabelaDePrecoParaAplicar?.id ?? null

			let params;
			if (novaVenda.isConsignado || novaVenda.isDemonstracao) {
				params = {
					isDevolucao: false,
					isDemonstracao: true,
					isVenda: false,
				}
			} else {
				params = {
					isDevolucao: false,
					isDemonstracao: false,
					isVenda: true,
				}
			}

			const modalidade = await this.findModalidadeDeVendaUseCase.findModalidadePadrao(loja.id, params)

			if (modalidade && modalidade.modalidade !== '') {
				novaVenda.modalidadeDeVenda = modalidade
			} else {
				novaVenda.modalidadeDeVenda = null
			}

			VendaModule.importarVenda({ vendaImportar: novaVenda, telaDeEmissaoDeNota })
			VendaModule.setVendaOriginadaDaPreVenda(true)
			const isAtacado = preVenda.tipoDePreVenda === 'atacado'

			return {
				preVendaId: preVenda.id,
				representante: preVenda.representante,
				isAtacado,
				tabelaDePreco: preVenda.tabelaDePreco ?? tabelaDePrecoParaAplicar ?? null,
			} as ResultadoImportacaoPreVenda

		} catch (e) {
			AlertModule.setError(e)
			throw e
		}
	}
}